import {useState} from 'react';
import {Link} from 'react-router-dom';
import {AlertTriangle, CheckCircle2, Eye, EyeOff, Loader2, Trash2} from 'lucide-react';
import {Button} from '../components/ui/button';
import {Card, CardContent, CardDescription, CardHeader, CardTitle} from '../components/ui/card';
import {Input} from '../components/ui/input';
import {Label} from '../components/ui/label';
import api from '../lib/api';
import {useAuth} from '../context/AuthContext';

const AccountDeletionPage = () => {
  const {logout} = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [deleted, setDeleted] = useState(false);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    if (!email.trim() || !password) {
      setError('Enter the email address and password for the account.');
      return;
    }
    if (!window.confirm('Permanently delete this account and all of its data? This cannot be undone.')) {
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/account-deletion', {
        email: email.trim().toLowerCase(),
        password,
      });
      await logout();
      setDeleted(true);
    } catch (requestError) {
      setError(
        requestError.response?.data?.detail
          || 'Account deletion could not be completed. Please try again.',
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background text-foreground px-4 py-8 sm:py-14">
      <main className="mx-auto w-full max-w-lg">
        <Card className="shadow-lg" data-testid="account-deletion-page">
          <CardHeader>
            <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-lg bg-destructive/10">
              <Trash2 className="h-5 w-5 text-destructive" aria-hidden="true" />
            </div>
            <CardTitle className="text-2xl">Delete account</CardTitle>
            <CardDescription>
              Verify your credentials to permanently delete your Culture Ken account.
            </CardDescription>
          </CardHeader>

          <CardContent>
            {deleted ? (
              <div className="space-y-5" data-testid="account-deletion-success">
                <div className="flex items-start gap-3 rounded-md border border-emerald-600/30 bg-emerald-600/10 p-4">
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600" />
                  <div>
                    <p className="font-semibold">Account deleted</p>
                    <p className="mt-1 text-sm text-muted-foreground">
                      Your account and personal data have been permanently removed.
                    </p>
                  </div>
                </div>
                <Button asChild className="w-full h-11">
                  <Link to="/">Return to Culture Ken</Link>
                </Button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} className="space-y-5">
                <div className="rounded-md border border-destructive/40 bg-destructive/10 p-4 text-destructive">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
                    <p className="text-sm font-semibold leading-6">
                      You will permanently lose all audio guides, credits, saved trips, collections, and progress. This cannot be undone.
                    </p>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deletion-email">Email address</Label>
                  <Input
                    id="deletion-email"
                    type="email"
                    autoComplete="email"
                    value={email}
                    onChange={(event) => setEmail(event.target.value)}
                    disabled={submitting}
                    required
                    className="h-11"
                    data-testid="account-deletion-email"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="deletion-password">Password</Label>
                  <div className="relative">
                    <Input
                      id="deletion-password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      value={password}
                      onChange={(event) => setPassword(event.target.value)}
                      disabled={submitting}
                      required
                      className="h-11 pr-11"
                      data-testid="account-deletion-password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((current) => !current)}
                      className="absolute inset-y-0 right-0 flex w-11 items-center justify-center text-muted-foreground"
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff /> : <Eye />}
                    </button>
                  </div>
                </div>

                {error ? (
                  <p className="text-sm font-medium text-destructive" role="alert" data-testid="account-deletion-error">
                    {error}
                  </p>
                ) : null}

                <p className="text-xs leading-5 text-muted-foreground">
                  Signed up with Google or passwordless sign-in?{' '}
                  <Link className="font-medium text-primary underline underline-offset-4" to="/forgot-password">
                    Set a password first
                  </Link>
                  , then return to this page.
                </p>

                <p className="text-xs leading-5 text-muted-foreground">
                  Deleting Culture Ken does not cancel subscriptions managed by the App Store or Google Play. Cancel those subscriptions in your store account to stop future renewals.
                </p>

                <Button
                  type="submit"
                  variant="destructive"
                  className="h-11 w-full"
                  disabled={submitting}
                  data-testid="account-deletion-submit"
                >
                  {submitting ? <Loader2 className="animate-spin" /> : <Trash2 />}
                  {submitting ? 'Deleting account...' : 'Delete account'}
                </Button>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
    </div>
  );
};

export default AccountDeletionPage;
